import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/app/api/client';
import { useAssistantAction } from '@/contexts/AssistantActionContext';
import { useToast } from '@/hooks/useToast';
import { dispatchAssistantActionResult } from '@/lib/assistant-events';

export const useCreateArticleAction = (onSuccess?: () => void) => {
  const navigate = useNavigate();
  const { registerActionHandler, unregisterActionHandler } = useAssistantAction();
  const { addToast } = useToast();

  const handleCreateArticle = useCallback(async (
    payload: { title: string; content?: string }, 
    action: { messageId?: string }
  ) => {
    if (!payload.title) {
      addToast('Cannot create article without a title.', 'error');
      return;
    }

    try {
      addToast('Creating your new article...', 'success');

      // Step 1: Create the article metadata
      const metaResponse = await apiClient.post('/api/articles', {
        meta: {
          title: payload.title,
        },
        state: 'draft',
      });

      const newArticle = metaResponse.data;
      if (!newArticle || !newArticle.uuid) {
        throw new Error('Invalid API response when creating article metadata.');
      }

      // Step 2: Save the initial content to the Durable Object
      if (payload.content) {
        await apiClient.post(`/api/articles/${newArticle.uuid}/content`, payload.content, {
          headers: { 'Content-Type': 'text/markdown' },
        });
      }

      // Step 3: Dispatch action result with link to augment the message
      if (action.messageId) {
        console.log('Dispatching action result for messageId:', action.messageId);
        dispatchAssistantActionResult(action.messageId, {
          type: 'link',
          url: `/articles/edit?id=${newArticle.uuid}`,
          label: 'Open Article'
        });
      }

      // Step 4: Also try to open in new tab (may be blocked)
      window.open(`/articles/edit?id=${newArticle.uuid}`, '_blank');

      // Step 5: Call the success callback if provided
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Failed to create article:', error);
      addToast('Failed to create article. Please try again.', 'error');
    }
  }, [navigate, addToast, onSuccess]);

  useEffect(() => {
    registerActionHandler('create_article', handleCreateArticle);
    return () => {
      unregisterActionHandler('create_article');
    };
  }, [registerActionHandler, unregisterActionHandler, handleCreateArticle]);
};
